import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Info, Loader2, Plus, Tag, X } from "lucide-react"

import AppShell from "@/components/tuw/AppShell"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Input } from "@/components/ui/input"
import { Slider } from "@/components/ui/slider"
import {
  RAIL_BAR_CLEARANCE,
  RAIL_CARD_BAND,
  RAIL_SHEET_BODY_CAP,
  RailBottomBar,
} from "@/components/tuw/post/PriceRail"
import { SERVICE_CONFIGS } from "@/lib/post/services"
import { addonPriceFields, ELIGIBLE_QA_QC, QA_DECLINED, QA_SELECTED } from "@/lib/post/addon"
import { fetchEstimate } from "@/lib/pricingCalculatorActions"

// Atrium item 10 — Tools: the Pricing Calculator. A free, multi-service cost
// estimator. Every number comes from the REAL server pricing path
// (PostingCalculator::Estimate), never a client-side rate table: the page holds
// per-service state, debounces a POST to /pricing-calculator/estimate, and
// renders the returned itemised lines + grand total. Card copy and the rail
// always agree because they read the SAME server response (tuw-app:P-001).
//
// The request body reuses the shipped Post-a-Project pricePayload() builders
// (SERVICE_CONFIGS) + the QA/QC addon merge (ELIGIBLE_QA_QC), so the calculator
// and the posting wizard price identically (tuw-app:P-007). The service catalog,
// field defs and first-paint estimate are prop-driven from
// CustomerPricingCalculatorPresenter — the backend owns the field set (PR-002).

const DEBOUNCE_MS = 250

function fmt(value) {
  return `$${Number(value ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

let CARD_SEQ = 1

// Seed a fresh card's state from the catalog field defaults so it prices on the
// very first render (matches the presenter's initial estimate).
function seedState(svc) {
  const state = {}
  for (const field of svc.fields) state[field.key] = field.default
  return state
}

function makeCard(svc) {
  return { uid: CARD_SEQ++, key: svc.key, state: seedState(svc) }
}

// The estimate request body for one card: reuse the shipped pricePayload builder,
// then merge the per-card QA/QC toggles for the three eligible services (PR-002).
// A builder that returns null (a required field cleared) collapses to a bare
// posting type so the server still returns an aligned $0 row.
function buildPayload(card, svc) {
  const cfg = SERVICE_CONFIGS[card.key]
  const payload = (cfg && cfg.pricePayload(card.state)) || { postingType: svc.postingType }

  if (ELIGIBLE_QA_QC.includes(card.key)) {
    // Reuse the shared addon mapper so the boolean-to-wire contract lives in ONE
    // place (tuw-app:P-007). The calculator's per-card toggles are simple booleans;
    // map them onto the enum shape addonPriceFields owns, then take only the QA/QC
    // keys (managed-services tier is not a calculator control).
    const addon = addonPriceFields({
      quality_assurance_addon: card.state.quality_assurance ? QA_SELECTED : QA_DECLINED,
      quality_control_enabled: card.state.quality_control ? QA_SELECTED : QA_DECLINED,
    })
    payload.qualityAssurance = addon.qualityAssurance
    payload.qualityControl = addon.qualityControl
  }

  return payload
}

// ── field renderers ───────────────────────────────────────────────────────────

function FieldLabel({ children }) {
  return <label className="mb-[7px] text-[11.5px] font-bold text-ink-2">{children}</label>
}

const CONTROL_CLASS =
  "w-full rounded-[10px] border border-line-2 bg-card px-3 py-[10px] text-[13.5px] text-foreground h-auto shadow-none dark:bg-card md:text-[13.5px] " +
  "focus:border-primary focus:outline-none focus:ring-[3px] focus:ring-[var(--red-soft)] focus-visible:border-primary focus-visible:ring-[var(--red-soft)]"

function SelectField({ field, value, onChange }) {
  return (
    <div className="flex flex-col">
      <FieldLabel>{field.label}</FieldLabel>
      <Select value={value ?? ""} onValueChange={(v) => onChange(field.key, v)}>
        <SelectTrigger className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {field.options.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

function NumberField({ field, value, onChange }) {
  return (
    <div className="flex flex-col">
      <FieldLabel>{field.label}</FieldLabel>
      <Input
        type="number"
        className={CONTROL_CLASS}
        value={value ?? 0}
        min={field.min}
        max={field.max}
        step={field.step}
        onChange={(e) => {
          const next = Math.min(field.max, Math.max(field.min, Number(e.target.value) || 0))
          onChange(field.key, next)
        }}
      />
    </div>
  )
}

function WordCountField({ field, value, onChange }) {
  return (
    <div className="col-span-full flex flex-col">
      <div className="mb-2 flex items-baseline justify-between">
        <label className="text-[11.5px] font-bold text-ink-2">{field.label}</label>
        <div className="font-display text-[14.5px] font-bold text-foreground">
          {Number(value ?? 0).toLocaleString()} words
        </div>
      </div>
      <Slider
        min={field.min}
        max={field.max}
        step={field.step}
        value={[value ?? field.min]}
        onValueChange={([v]) => onChange(field.key, v)}
      />
    </div>
  )
}

function ToggleField({ field, value, onChange }) {
  const on = !!value
  return (
    <div className="flex flex-col justify-end">
      <button
        type="button"
        role="switch"
        aria-checked={on}
        onClick={() => onChange(field.key, !on)}
        className="flex items-center gap-[11px] text-left"
      >
        <span
          className={
            "relative h-[22px] w-[38px] shrink-0 rounded-full border transition-colors " +
            (on ? "border-foreground bg-foreground" : "border-line-2 bg-secondary")
          }
        >
          <span
            className={
              "absolute top-[2px] left-[2px] size-4 rounded-full bg-white shadow transition-transform " +
              (on ? "translate-x-4" : "")
            }
          />
        </span>
        <span>
          <b className="block text-[13px] font-semibold text-foreground">{field.label}</b>
          {field.sub && <span className="text-[11.5px] text-ink-3">{field.sub}</span>}
        </span>
      </button>
    </div>
  )
}

function Field({ field, value, onChange }) {
  if (field.type === "tier" || field.type === "select") return <SelectField field={field} value={value} onChange={onChange} />
  if (field.type === "number") return <NumberField field={field} value={value} onChange={onChange} />
  if (field.type === "wordcount") return <WordCountField field={field} value={value} onChange={onChange} />
  if (field.type === "toggle") return <ToggleField field={field} value={value} onChange={onChange} />
  return null
}

// ── project card ──────────────────────────────────────────────────────────────

function ProjectCard({ index, card, svc, services, service, canRemove, onServiceChange, onFieldChange, onRemove }) {
  const priceNode = service ? (
    service.from ? (
      <div className="font-display text-[17px] font-bold text-foreground">
        <span className="font-sans text-[11px] font-semibold text-ink-3">From</span> {fmt(service.total)}
      </div>
    ) : (
      <div className="font-display text-[17px] font-bold text-foreground">{fmt(service.total)}</div>
    )
  ) : null

  return (
    <div className="mb-[14px] overflow-hidden rounded-2xl border border-border bg-card">
      <div className="flex items-center gap-[13px] border-b border-border px-[18px] py-[15px] max-[561px]:flex-wrap">
        <div className="grid size-[34px] shrink-0 place-items-center rounded-[10px] border border-border bg-secondary font-display text-[12px] font-bold text-ink-2">
          {index + 1}
        </div>
        <Select value={card.key} onValueChange={(v) => onServiceChange(card.uid, v)}>
          <SelectTrigger className="flex-1 text-sm font-semibold">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {services.map((option) => (
              <SelectItem key={option.key} value={option.key}>
                {option.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="ml-auto shrink-0 text-right">
          <div className="text-[10px] font-bold tracking-[0.06em] text-ink-3 uppercase">Subtotal</div>
          {priceNode}
        </div>
        {canRemove && (
          <button
            type="button"
            title="Remove"
            onClick={() => onRemove(card.uid)}
            className="grid size-8 shrink-0 place-items-center rounded-[9px] border border-border bg-card text-ink-3 hover:border-[var(--red-soft)] hover:bg-[var(--red-soft)] hover:text-primary"
          >
            <X className="size-[15px]" strokeWidth={2} />
          </button>
        )}
      </div>

      <div data-testid="calc-card-body" className="grid grid-cols-1 gap-4 p-[18px] min-[561px]:grid-cols-2">
        {svc.note && (
          <div className="col-span-full flex items-center gap-2 rounded-[9px] border border-border bg-accent px-3 py-[9px] text-[12px] text-ink-3">
            <Info className="size-[14px] shrink-0 text-ink-3" strokeWidth={1.9} />
            <span>{svc.note}</span>
          </div>
        )}
        {svc.fields.map((field) => (
          <Field
            key={field.key}
            field={field}
            value={card.state[field.key]}
            onChange={(key, value) => onFieldChange(card.uid, key, value)}
          />
        ))}
      </div>
    </div>
  )
}

// ── estimate rail ─────────────────────────────────────────────────────────────

function RailRow({ index, name, service }) {
  if (!service) return null
  const amount = service.from ? (
    <>
      <span className="font-sans text-[10px] font-semibold text-ink-3">From</span> {fmt(service.total)}
    </>
  ) : (
    fmt(service.total)
  )

  return (
    <div className="border-b border-border py-[11px] last:border-b-0">
      <div className="flex items-start justify-between gap-3">
        <div className="text-[12.5px] font-semibold text-foreground">
          {index + 1}. {name}
        </div>
        <div className="font-display text-[13.5px] font-bold whitespace-nowrap text-foreground">{amount}</div>
      </div>
      <div className="mt-[6px] space-y-[3px]">
        {service.lines.map((line, i) => (
          <div key={i} className="flex items-center justify-between gap-3 text-[11.5px] text-ink-3">
            <span className="truncate">{line.label}</span>
            <span className="whitespace-nowrap tabular-nums">{fmt(line.amount)}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

// S-TOOLS-PRICING-CALC's rail seam. Above 1081 it is the sticky estimate card
// below, unchanged; at or below 1080 it becomes the SAME fixed bottom bar + DS
// bottom sheet the posting wizards use (P8-H5 "Footlights"), through the shared
// primitives — no calculator-local drawer. Per the frozen contract this bar
// carries the TOTAL ONLY: no "Free to post" hint (the card's own reassurance
// note says it inside the sheet) and no delivery claim, which this page has
// never made.
function EstimateRail({ services, cardServices, aggregate, promo, onPromoChange, onApplyPromo, loading }) {
  const anyRange = aggregate && aggregate.subtotalMax > aggregate.subtotalMin
  const headline = aggregate ? aggregate.total : 0

  const card = (inSheet) => (
    <EstimateCard
      services={services}
      cardServices={cardServices}
      aggregate={aggregate}
      promo={promo}
      onPromoChange={onPromoChange}
      onApplyPromo={onApplyPromo}
      loading={loading}
      inSheet={inSheet}
    />
  )

  return (
    <>
      <div
        data-testid="rail-card"
        className={`min-[1081px]:sticky min-[1081px]:top-[18px] ${RAIL_CARD_BAND}`}
      >
        {card(false)}
      </div>
      <RailBottomBar
        eyebrow="Estimated total"
        from={anyRange ? "From" : null}
        value={fmt(headline)}
        label="Show estimate breakdown"
      >
        {card(true)}
      </RailBottomBar>
    </>
  )
}

function EstimateCard({ services, cardServices, aggregate, promo, onPromoChange, onApplyPromo, loading, inSheet }) {
  const anyRange = aggregate && aggregate.subtotalMax > aggregate.subtotalMin
  const hasDiscount = aggregate && aggregate.discount > 0 && aggregate.discountValid
  const headline = aggregate ? aggregate.total : 0

  return (
    <div
      className={
        "overflow-hidden rounded-2xl border border-border bg-card" +
        (inSheet ? " rounded-b-none border-b-0" : "")
      }
    >
      <div className="flex items-center justify-between border-b border-border px-5 pt-[18px] pb-[14px]">
        <div className={inSheet ? "pr-8" : undefined}>
          <h2 className="font-display text-sm font-bold text-foreground">Your estimate</h2>
          <p className="mt-[2px] text-[11.5px] text-ink-3">Posting is free — you pay on attach.</p>
        </div>
        {loading && <Loader2 className="size-4 animate-spin text-ink-3" />}
      </div>

      <div
        data-testid={inSheet ? "rail-sheet-body" : undefined}
        className={"px-5 py-1" + (inSheet ? ` ${RAIL_SHEET_BODY_CAP}` : "")}
      >
        {cardServices.length === 0 ? (
          <div className="px-1 py-6 text-center text-[12.5px] text-ink-3">Add a service to see an estimate.</div>
        ) : (
          cardServices.map((entry, i) => (
            <RailRow key={entry.uid} index={i} name={entry.name} service={services[i]} />
          ))
        )}
      </div>

      <div className="border-t border-border bg-accent px-5 py-4">
        {hasDiscount && (
          <>
            <div className="flex items-baseline justify-between text-[12px]">
              <span className="text-ink-3">Subtotal</span>
              <span className="font-display font-bold text-foreground">{fmt(aggregate.subtotal)}</span>
            </div>
            <div className="mt-1 flex items-baseline justify-between text-[12px]">
              <span className="text-primary">Discount</span>
              <span className="font-display font-bold text-primary">−{fmt(aggregate.discount)}</span>
            </div>
          </>
        )}

        <div className="mt-[6px] flex items-baseline justify-between">
          <span className="text-xs font-bold tracking-[0.04em] text-ink-3 uppercase">Estimate</span>
          <b className="font-display text-[30px] font-bold tracking-[-0.5px] text-foreground">
            {anyRange && <span className="font-sans text-[13px] font-semibold text-ink-3">From </span>}
            {fmt(headline)}
          </b>
        </div>
        {anyRange && (
          <div className="mt-1 text-right text-[11.5px] text-ink-3">
            Estimated range {fmt(aggregate.totalMin)} – {fmt(aggregate.totalMax)}
          </div>
        )}

        <div className="mt-[14px] flex gap-2">
          <Input
            value={promo.input}
            onChange={(e) => onPromoChange(e.target.value)}
            placeholder="Discount code"
            maxLength={24}
            className="flex-1 rounded-[9px] border border-line-2 bg-card px-[11px] py-[9px] text-[12.5px] text-foreground uppercase h-auto shadow-none dark:bg-card md:text-[12.5px] focus:border-primary focus:outline-none focus:ring-[3px] focus:ring-[var(--red-soft)] focus-visible:border-primary focus-visible:ring-[var(--red-soft)]"
          />
          <button
            type="button"
            onClick={onApplyPromo}
            className="rounded-[9px] border border-line-2 bg-card px-[14px] text-[12px] font-semibold text-ink-2 hover:bg-secondary"
          >
            Apply
          </button>
        </div>
        {promo.applied && (
          <p
            className={
              "mt-2 flex items-center gap-[6px] text-[11.5px] font-semibold " +
              (aggregate && aggregate.discountValid ? "text-done" : "text-primary")
            }
          >
            <Tag className="size-[13px]" strokeWidth={2} />
            {aggregate && aggregate.discountValid
              ? `Code ${promo.applied} applied`
              : `Code ${promo.applied} doesn’t apply to this estimate`}
          </p>
        )}
      </div>

      <div className="flex gap-[10px] px-5 pt-[13px] pb-[18px]">
        <Info className="mt-[1px] size-[15px] shrink-0 text-ink-3" strokeWidth={1.9} />
        <p className="text-[11.5px] leading-[1.5] text-ink-3">
          <b className="font-semibold text-ink-2">Nothing is charged now.</b> Posting a project is free; you only pay
          this estimate once you choose a freelancer to start the work.
        </p>
      </div>
    </div>
  )
}

// ── page ──────────────────────────────────────────────────────────────────────

export default function PricingCalculator({ calculator }) {

  const services = calculator?.services ?? []
  const byKey = useMemo(() => Object.fromEntries(services.map((svc) => [svc.key, svc])), [services])
  const defaultKey = calculator?.defaultService ?? services[0]?.key
  const addKey = byKey.cover_design ? "cover_design" : defaultKey

  const [cards, setCards] = useState(() => (defaultKey ? [makeCard(byKey[defaultKey])] : []))
  const [estimate, setEstimate] = useState(calculator?.initialEstimate ?? null)
  const [loading, setLoading] = useState(false)
  const [promo, setPromo] = useState({ input: "", applied: null })

  const abortRef = useRef(null)

  const cardServices = useMemo(() => cards.map((card) => ({ uid: card.uid, name: byKey[card.key]?.name })), [cards, byKey])

  const recompute = useCallback(
    (nextCards, code) => {
      if (nextCards.length === 0) {
        setEstimate(null)
        return
      }

      const projects = nextCards.map((card) => buildPayload(card, byKey[card.key]))

      if (abortRef.current) abortRef.current.abort()
      const controller = new AbortController()
      abortRef.current = controller

      setLoading(true)
      fetchEstimate(projects, code, { signal: controller.signal }).then((result) => {
        if (controller.signal.aborted) return
        if (result) setEstimate(result)
        setLoading(false)
      })
    },
    [byKey],
  )

  // Debounced recompute on any state change (cards or applied code).
  useEffect(() => {
    const handle = setTimeout(() => recompute(cards, promo.applied), DEBOUNCE_MS)
    return () => clearTimeout(handle)
  }, [cards, promo.applied, recompute])

  function updateCard(uid, updater) {
    setCards((current) => current.map((card) => (card.uid === uid ? updater(card) : card)))
  }

  function handleServiceChange(uid, nextKey) {
    const svc = byKey[nextKey]
    if (!svc) return
    updateCard(uid, (card) => ({ ...card, key: nextKey, state: seedState(svc) }))
  }

  function handleFieldChange(uid, key, value) {
    updateCard(uid, (card) => ({ ...card, state: { ...card.state, [key]: value } }))
  }

  function handleRemove(uid) {
    setCards((current) => current.filter((card) => card.uid !== uid))
  }

  function handleAdd() {
    const svc = byKey[addKey]
    if (svc) setCards((current) => [...current, makeCard(svc)])
  }

  const estimateServices = estimate?.services ?? []
  const aggregate = estimate?.aggregate ?? null

  return (
    <>
      <div className="w-full max-w-[1180px] px-[26px] pt-6 pb-16">
        <div className="mb-5">
          <h1 className="font-display text-2xl font-bold tracking-[-0.4px] text-foreground">Pricing Calculator</h1>
          <p className="mt-[5px] max-w-[600px] text-[13px] leading-[1.55] text-ink-3">
            Estimate the cost of any service before you post it. Stack several services to price out a full project —
            the rates match what you’ll pay when you attach a freelancer.
          </p>
        </div>

        <div className={`grid grid-cols-1 items-start gap-5 min-[1081px]:grid-cols-[1fr_340px] ${RAIL_BAR_CLEARANCE}`}>
          <div>
            <div>
              {cards.map((card, index) => (
                <ProjectCard
                  key={card.uid}
                  index={index}
                  card={card}
                  svc={byKey[card.key]}
                  services={services}
                  service={estimateServices[index]}
                  canRemove={cards.length > 1}
                  onServiceChange={handleServiceChange}
                  onFieldChange={handleFieldChange}
                  onRemove={handleRemove}
                />
              ))}
            </div>

            <button
              type="button"
              onClick={handleAdd}
              className="flex w-full items-center justify-center gap-2 rounded-[13px] border border-dashed border-line-2 bg-accent px-3 py-[13px] text-[13px] font-semibold text-ink-2 hover:border-ink-3 hover:bg-secondary hover:text-foreground"
            >
              <Plus className="size-4" strokeWidth={2} />
              Add another service
            </button>

            <div className="mt-[6px] flex gap-[13px] rounded-[14px] border border-border bg-card px-[18px] py-[15px]">
              <div className="grid size-[34px] shrink-0 place-items-center rounded-[10px] border border-border bg-secondary text-ink-2">
                <Info className="size-[17px]" strokeWidth={1.8} />
              </div>
              <p className="text-[12px] leading-[1.55] text-ink-3">
                <b className="font-semibold text-ink-2">These are estimates at current freelancer rates.</b> The exact
                total is confirmed when you attach a writer — for{" "}
                <b className="font-semibold text-ink-2">All&nbsp;Tier&nbsp;Access</b> you pay the rate of whoever you
                choose, so those show a “from” range.
              </p>
            </div>
          </div>

          <EstimateRail
            services={estimateServices}
            cardServices={cardServices}
            aggregate={aggregate}
            promo={promo}
            loading={loading}
            onPromoChange={(value) => setPromo((current) => ({ ...current, input: value }))}
            onApplyPromo={() => setPromo((current) => ({ ...current, applied: current.input.trim() || null }))}
          />
        </div>
      </div>
    </>
  )
}

PricingCalculator.layout = (page) => (
  <AppShell active="Pricing Calculator" crumb={["Tools", "Pricing Calculator"]}>
    {page}
  </AppShell>
)
